/**
 * artworkUploadContract -- single client-side source of truth for the
 * /api/upload_image contract.
 *
 * Mirrors the server-side enforcement in api/src/endpoints/upload.py
 * (ALLOWED_UPLOAD_TYPES + MAX_UPLOAD_BYTES) -- keep the two in sync.
 *
 * Why this exists: before this module, every upload surface hardcoded
 * its own rules. AddArtworkWizard had a 7-extension allowlist + 50 MB
 * cap; EditorArtworkPicker and UploadArtworkModal accepted `image/*`
 * with no size check at all, so a 2 GB .bmp sailed past the client and
 * (pre-hardening) past the server too. All artwork upload surfaces
 * must consume these exports so users see one contract with one error
 * copy everywhere.
 *
 * The client-side check is a UX pre-flight (instant feedback, no wasted
 * round trip). The backend enforces the same contract authoritatively.
 */

/**
 * Allowed file extensions, lowercase, dot-prefixed. Union of the web
 * image formats (store-editor / storefront pickers) and the print
 * artwork formats (Decoration Library wizards).
 */
export const ARTWORK_UPLOAD_EXTENSIONS = [
    '.svg',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.pdf',
    '.ai',
    '.eps',
    // Embroidery formats: .dst (Tajima stitch file) and .emb (Wilcom
    // design file). Stored server-side as octet-stream (download only).
    '.dst',
    '.emb',
]

/** Value for a file input's ``accept`` attribute. */
export const ARTWORK_UPLOAD_ACCEPT = ARTWORK_UPLOAD_EXTENSIONS.join(',')

/** Human-readable list for hint + error copy. */
export const ARTWORK_UPLOAD_ACCEPT_LABEL = ARTWORK_UPLOAD_EXTENSIONS.join(', ')

/** Max upload size in bytes (50 MB -- matches the backend cap). */
export const ARTWORK_UPLOAD_MAX_BYTES = 50 * 1024 * 1024

/**
 * Max SVG size in bytes (10 MB -- matches the backend MAX_SVG_BYTES).
 * SVG intake costs far more per byte than a raster, so it is capped lower.
 */
export const ARTWORK_SVG_MAX_BYTES = 10 * 1024 * 1024

/** Shared error copy. Exported so components/tests never fork the text. */
export const ARTWORK_UPLOAD_ERROR_REQUIRED = 'Choose an artwork file.'
export const ARTWORK_UPLOAD_ERROR_TYPE =
    `Unsupported file type. Allowed: ${ARTWORK_UPLOAD_ACCEPT_LABEL}.`
export const ARTWORK_UPLOAD_ERROR_SIZE =
    'File is too large. Maximum size is 50 MB.'
export const ARTWORK_SVG_ERROR_SIZE =
    'SVG files must be 10 MB or smaller. Simplify the artwork or export a smaller SVG.'

/**
 * Copy for a zero-byte / unreadable file.
 *
 * WHY THIS EXISTS (root cause established live 2026-08-24). A file
 * picked from Google Drive File Stream, iCloud Drive, or OneDrive
 * Files On-Demand may be a PLACEHOLDER that has never been
 * materialized to local disk. The picker shows it with the right name
 * and the right icon; the browser hands the page a ``File`` with that
 * name and ZERO bytes. The upload then fails server-side with an
 * honest 400 (``No file provided.`` / ``file is required``) that the
 * UI previously mistranslated into "timeout exceeded" and "Something
 * went wrong reading that file. Try again, or try a different file."
 *
 * Both of those were wrong, and the second was actively harmful: the
 * failure is not transient and the SAME file uploads fine once it is
 * downloaded, so "try a different file" points away from the fix. That
 * miscue cost two debugging sessions and three wrong root causes.
 *
 * All three sync clients behave identically here, so the copy names
 * all three -- naming Google Drive alone sends iCloud and OneDrive
 * users looking in the wrong place.
 */
export const ARTWORK_UPLOAD_ERROR_EMPTY =
    'That file appears to be empty or unreadable. If it is stored in ' +
    'Google Drive, iCloud Drive, or OneDrive, make sure it is downloaded ' +
    'to your computer first, then try again.'

const _EXTENSION_SET = new Set(ARTWORK_UPLOAD_EXTENSIONS)

export function extensionOf(filename) {
    if (typeof filename !== 'string') return ''
    const dot = filename.lastIndexOf('.')
    if (dot < 0) return ''
    return filename.slice(dot).toLowerCase()
}

/**
 * Extensions that denote an embroidery stitch/design source: .dst
 * (Tajima) and .emb (Wilcom). A file with one of these can ONLY be
 * decorated by embroidery (a stitch file is embroidery by definition).
 *
 * Mirrors the backend rule in
 * api/src/services/artwork_format.py::EMBROIDERY_SOURCE_EXTENSIONS.
 * Keep the two in sync -- the backend guard is the authority; this
 * powers the method-selection UX lock (PR-5 / WA-7c).
 */
export const EMBROIDERY_SOURCE_EXTENSIONS = ['.dst', '.emb']

/**
 * True when ``filename`` names an embroidery stitch/design source
 * (.dst/.emb). Used by the bind-surface method picker to lock the
 * method to embroidery.
 *
 * @param {string|null|undefined} filename
 * @returns {boolean}
 */
export function isEmbroiderySource(filename) {
    return EMBROIDERY_SOURCE_EXTENSIONS.includes(extensionOf(filename))
}

/**
 * Vector formats whose file has no pixel resolution: the file record says
 * "Vector" for them rather than a DPI. Mirrors the backend's handling in
 * api/src/services/artwork_file_metadata_service.py (SVG/AI/EPS keep
 * dpi NULL).
 */
export const VECTOR_FILE_EXTENSIONS = ['.svg', '.ai', '.eps']

/** What an artwork's file is measured in (see ``artworkFileKind``). */
export const ARTWORK_FILE_KIND = Object.freeze({
    STITCH: 'stitch',
    VECTOR: 'vector',
    RASTER: 'raster',
})

/**
 * Classify an artwork file by how it is measured -- the ONE place the
 * file record (and anything else that needs it) decides this:
 *
 * * ``stitch`` -- a .dst/.emb (``EMBROIDERY_SOURCE_EXTENSIONS``): a
 *   physical design size and a stitch count, never pixels.
 * * ``vector`` -- SVG/AI/EPS: resolution-independent.
 * * ``raster`` -- everything else: pixel dimensions and a DPI.
 *
 * Pass the CURRENT file's name (the header's ``original_filename``): a
 * version owns its file type.
 *
 * @param {string|null|undefined} filename
 * @returns {'stitch'|'vector'|'raster'}
 */
export function artworkFileKind(filename) {
    if (isEmbroiderySource(filename)) return ARTWORK_FILE_KIND.STITCH
    if (VECTOR_FILE_EXTENSIONS.includes(extensionOf(filename))) {
        return ARTWORK_FILE_KIND.VECTOR
    }
    return ARTWORK_FILE_KIND.RASTER
}

/**
 * Validate a File against the shared upload contract.
 *
 * @param {File|null|undefined} file
 * @returns {{ok: boolean, error: string|null}} ``error`` carries the
 *   shared copy when ``ok`` is false.
 */
export function isEmptyFile(file) {
    // A MISSING file is not an empty one -- it has its own message
    // (ARTWORK_UPLOAD_ERROR_REQUIRED). Offering a cloud-placeholder
    // hint for "you have not chosen anything yet" would be exactly the
    // confidently-wrong explanation this module exists to prevent.
    if (!file) return false
    return file.size === 0
}

export function validateArtworkFile(file) {
    if (!file) {
        return { ok: false, error: ARTWORK_UPLOAD_ERROR_REQUIRED }
    }
    // Emptiness is checked BEFORE the extension, on purpose. A
    // placeholder .dst is a perfectly supported type -- reporting
    // "unsupported file type" for it is what started the format
    // wild-goose chase. Byte-level reality outranks the filename.
    if (isEmptyFile(file)) {
        return { ok: false, error: ARTWORK_UPLOAD_ERROR_EMPTY }
    }
    if (!_EXTENSION_SET.has(extensionOf(file.name))) {
        return { ok: false, error: ARTWORK_UPLOAD_ERROR_TYPE }
    }
    if (
        extensionOf(file.name) === '.svg' &&
        file.size > ARTWORK_SVG_MAX_BYTES
    ) {
        return { ok: false, error: ARTWORK_SVG_ERROR_SIZE }
    }
    if (file.size > ARTWORK_UPLOAD_MAX_BYTES) {
        return { ok: false, error: ARTWORK_UPLOAD_ERROR_SIZE }
    }
    return { ok: true, error: null }
}
